import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import GemstoneCard from "../components/GemstoneCard";
import Seo from "../components/Seo";
import { collectionPageSchema, breadcrumbSchema } from "../lib/schema";

const CATS = ["All", "Diamond", "Sapphire", "Ruby", "Emerald", "Rare"];

const Collection = () => {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [searchParams, setSearchParams] = useSearchParams();
  const queryText = searchParams.get("q") || "";

  useEffect(() => {
    const params = {};
    if (cat !== "All") params.category = cat;
    if (queryText) params.q = queryText;
    api.get("/gemstones", { params }).then(({ data }) => setItems(data));
  }, [cat, queryText]);

  const clearSearch = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const seoTitle = cat === "All" ? "Collection · Investment-Grade Gemstones" : `${cat} Collection · OPIKJEWELS`;
  const seoDesc =
    cat === "All"
      ? "Investment-grade gemstones — diamonds, sapphires, rubies, emeralds, and rare stones. Every specimen is laboratory-certified with full provenance. Curated by OPIKJEWELS Jakarta."
      : `Curated ${cat.toLowerCase()} collection. Every ${cat.toLowerCase()} is laboratory-certified with full provenance and lifetime warranty. Investment-grade selections from OPIKJEWELS.`;

  return (
    <div data-testid="collection-page" className="pt-32">
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={cat === "All" ? "/collection" : `/collection?category=${cat}`}
        jsonLd={[
          collectionPageSchema({
            name: seoTitle,
            description: seoDesc,
            path: "/collection",
            items,
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Collection", url: "/collection" },
            ...(cat !== "All" ? [{ name: cat }] : []),
          ]),
        ]}
      />
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mb-16">
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Collection</p>
        <h1 className="font-heading font-light text-5xl md:text-7xl leading-[1.05] max-w-4xl">Gemstones of singular standing.</h1>
        <p className="mt-6 text-white/60 max-w-2xl leading-relaxed">Curated specimens — every stone authenticated by leading laboratories. Discreet acquisition available via concierge.</p>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-wrap gap-3 mb-12 border-b border-white/10 pb-8">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase()}`}
              onClick={() => setCat(c)}
              className={`font-accent text-[11px] tracking-[0.28em] uppercase px-5 py-3 border transition-all duration-500 ${
                cat === c ? "border-gold text-gold" : "border-white/15 text-white/50 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        {queryText && (
          <div data-testid="search-pill" className="mb-8 flex items-center gap-3 flex-wrap">
            <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40">
              Searching for
            </span>
            <span className="inline-flex items-center gap-2 bg-gold/10 border border-gold/40 text-gold px-3 py-1.5">
              {queryText}
              <button
                data-testid="search-clear"
                onClick={clearSearch}
                aria-label="Clear search"
                className="hover:text-ivory"
              >
                ×
              </button>
            </span>
            <span className="text-white/40 text-sm">
              {items.length} result{items.length === 1 ? "" : "s"}
            </span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 pb-32">
          {items.map((g) => <GemstoneCard key={g.id} gem={g} />)}
        </div>
        {items.length === 0 && (
          <div className="text-white/40 text-center py-32">
            {queryText
              ? <>No gemstones matched <span className="text-gold">"{queryText}"</span>. Try a broader term or browse by category above.</>
              : "No gemstones in this category."}
          </div>
        )}
      </section>
    </div>
  );
};

export default Collection;
